/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Form, Space } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreateOauthClientRequest, CreateOauthClientResponse2 } from '@/client';
import { ContentContainer } from '@/components/ContentContainer';
import { PageHeader } from '@/components/PageHeader';
import { useMessage } from '@/hooks/message';
import { useCreateOAuthClientMutation } from '@/hooks/mutation/useCreateOAuthClientMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ClientFormActions } from '@/pages/OAuthClients/ClientFormActions';
import { ClientNameSection } from '@/pages/OAuthClients/ClientNameSection';
import { OAuthClientCreatedModal } from '@/pages/OAuthClients/OAuthClientCreatedModal';
import { RedirectUrisSection } from '@/pages/OAuthClients/RedirectUrisSection';
import { SignatureAlgSection } from '@/pages/OAuthClients/SignatureAlgSection';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export function CreateOAuthClientPage() {
  const { t } = useMimicTranslation('oauthClients');
  const [form] = Form.useForm<CreateOauthClientRequest>();
  const navigate = useNavigate();
  const [message] = useMessage();
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [createdClient, setCreatedClient] = useState<CreateOauthClientResponse2 | undefined>();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {
      message.error(t('failedCopy'));
    });
  };

  const { mutate: createOAuthClient, isPending } = useCreateOAuthClientMutation({
    onError: (error) => {
      message.error(t('creationError'));
      if (error instanceof Error) {
        // Handle validation errors - just show error message
        form.setFields([
          {
            name: 'name',
            errors: [error.message],
          },
        ]);
      }
    },
    onSuccess: (client) => {
      message.success(t('clientCreated'));
      setCreatedClient(client);
    },
  });

  const onFinish = (values: CreateOauthClientRequest) => {
    createOAuthClient({
      name: values.name.trim(),
      redirectUris: values.redirectUris.map((uri) => uri.trim()),
      idTokenSignatureAlg: values.idTokenSignatureAlg,
    });
  };

  const handleModalClose = () => {
    setCreatedClient(undefined);
    navigate(`/tenants/${tenantID}/oauth-clients`);
  };

  if (!tenantID) return null;

  return (
    <ContentContainer width="100%">
      <Container gap="lg" style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
        <Container gap="sm">
          <PageHeader title={t('createClientModal.title')} level={2} />
          <Text type="subtle" style={{ fontSize: '15px', lineHeight: '1.4' }}>
            {t('description')}
          </Text>
        </Container>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ width: '100%' }}
          disabled={isPending}
          initialValues={{ redirectUris: [''] }}
        >
          <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <ClientNameSection />

            <RedirectUrisSection />

            <SignatureAlgSection />

            <ClientFormActions
              onCancel={() => navigate(`/tenants/${tenantID}/oauth-clients`)}
              isSubmitting={isPending}
            />
          </Space>
        </Form>
      </Container>
      {createdClient && (
        <OAuthClientCreatedModal
          createdClient={createdClient}
          onClose={handleModalClose}
          copyToClipboard={copyToClipboard}
        />
      )}
    </ContentContainer>
  );
}
